import React, { FC, useEffect, useState } from "react";

// UI
import { Heading } from "@UI/Components/Heading/Heading";
import { Container } from "@UI/Components/Container/Container";
import { Box } from "@UI/Components/Box/Box";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import {
  Wizard,
  WizardHeader,
  WizardContent,
  WizardNav,
} from "@Shared/Components/Layouts/Wizard/Wizard";

// i18n
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "./messages";

// Core
import { FAQSection } from "@modules/CustomerSupport/Components/FAQSection/FAQSection";
import { useCheckout } from "@modules/Purchasing/hooks/useCheckout";
import { useCurrentBasket } from "@modules/Purchasing/hooks/useCurrentBasket";
import { useOrderPreview } from "@modules/Purchasing/hooks/useOrderPreview";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { Card } from "@UI/Components/Card/Card";
import { isEmpty } from "lodash-es";
import { Navigate } from "@Libs/Router/Components/Navigate/Navigate";
import { BackLink } from "@Shared/Components/BackLink/BackLink";
import { Stack } from "@UI/Components/Stack/Stack";
import { ResponsiveFixedButton } from "@UI/Components/ResponsiveFixedButton/ResponsiveFixedButton";
import { useMetadata } from "@Libs/Events/hooks/useMetadata";
import { ServiceFeeSubscriptionCTA } from "@modules/ServiceFees/Components/ServiceFeeSubscriptionCTA/ServiceFeeSubscriptionCTA";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { OrderDescription } from "@modules/Purchasing/Components/OrderDescription/OrderDescription";
import { OppositionLink } from "@Shared/Components/OppositionLink/OppositionLink";
import { useParams } from "@Libs/Router/hooks/useParams";
import { useUserPaymentMethods } from "@modules/PaymentProcessing/hooks/useUserPaymentMethods";
import { OrderPaid } from "@modules/Purchasing/Components/OrderDescription/OrderPaid/OrderPaid";
import { IAppRoute } from "@App/types";
import { v4 as uuidv4 } from "uuid";
import { RingtwicePlusAdvantagesModal } from "@modules/Subscriptions/Components/SubscriptionAdvantagesModal/RingtwicePlusAdvantagesModal";
import { processPayment } from "@modules/PaymentProcessing/services/payments";
import { PaymentMethodType } from "@modules/PaymentProcessing/types";
import { useRoutes } from "@Libs/Router/hooks/useRoutes";
import { useService } from "@modules/ServiceProcessing/hooks/useService";

interface ICheckOrderProps {
  allowedPaymentMethods?: PaymentMethodType[];
}

export const CheckOrder: FC<ICheckOrderProps> = (props) => {
  const { allowedPaymentMethods } = props;
  const isAllowedPaymentMethodSingle =
    Array.isArray(allowedPaymentMethods) && allowedPaymentMethods.length === 1;
  const [isOpen, setIsOpen] = useState<boolean>();
  const { isMobile } = useMediaQuery();
  const {
    metadata: { context },
  } = useMetadata();
  const { generateRoute } = useRoutes();

  const {
    backRoute,
    onPageMount,
    navigateToStep,
    rootRoute,
    onLeave,
    onSuccess,
    state: { relatedServiceId },
  } = useCheckout();

  const { currentBasket, isSavingBasket, isLoading, hasBasketSubscription } =
    useCurrentBasket();

  const { serviceOfferId } = useParams(["SERVICE_OFFER"]);
  const { service } = useService(serviceOfferId);

  const {
    orderPreview,
    isLoading: isOrderPreviewLoading,
    isAlreadyPaid,
    isAlreadyProcessed,
  } = useOrderPreview({
    basketId: currentBasket?.id,
    items: currentBasket?.items,
    enabled: !(isSavingBasket || isLoading),
  });

  const { currentUser } = useCurrentUser({
    includes: ["currentSubscription"],
  });

  const { getPreferredCreditCard } = useUserPaymentMethods();

  const navigate = useNavigate();

  const handleConfirm = () => {
    if (orderPreview?.itemsTotalCents === 0) {
      handleNoAmountToPay(currentBasket?.id.toString());
      return;
    }

    const route: IAppRoute[] = Array.isArray(rootRoute.to)
      ? [...rootRoute.to, "PAY", "CONFIGURE_PAYMENT"]
      : [rootRoute.to as IAppRoute, "PAY", "CONFIGURE_PAYMENT"];

    const options = {
      params: { serviceOfferId },
      queryParams: {
        paymentMethod: isAllowedPaymentMethodSingle
          ? allowedPaymentMethods[0]
          : "credit_card",
      },
    };

    /**
     * if available payment method is only one, it will skip payment method list page and navigate directly to payment configuration page
     */
    if (isAllowedPaymentMethodSingle) {
      navigate(route, options);
    } else if (!getPreferredCreditCard()) navigateToStep("PAY");
    else {
      navigate(route, options); // paymentMethod inside "options" default to "credit_card"
    }
  };

  const checkTitle = () => {
    if (context === "serviceClosureFlow") {
      return { ...messages.confirmCheckout };
    } else return { ...messages.checkYourOrder };
  };

  useEffect(() => {
    if (!isOrderPreviewLoading && !isEmpty(orderPreview?.items)) {
      onPageMount?.("CHECK_ORDER");
    }
  }, [orderPreview?.items, isOrderPreviewLoading]);

  if (isOrderPreviewLoading || isSavingBasket || isLoading) {
    return <LoadingScreen />;
  }

  if (isEmpty(currentBasket?.items)) {
    return <Navigate {...backRoute} />;
  }

  if (isAlreadyPaid || isAlreadyProcessed) {
    return (
      <Card>
        <OrderPaid />
      </Card>
    );
  }

  const handleServiceFeeSubscriptionCTAClick = (e) => {
    e.preventDefault();
    setIsOpen(true);
    onPageMount?.("SUBSCRIBE");
  };

  const handleContinueFromModal = () => {
    navigate(["SERVICE_OFFERS", "SERVICE_OFFER", "CLOSE", "SUBSCRIBE"], {
      params: { serviceOfferId },
      queryParams: { resourceId: uuidv4() },
    });
  };

  const handleNoAmountToPay = async (basketId) => {
    try {
      const response = await processPayment({
        paymentProvider: "company_operations",
        paymentMethod: PaymentMethodType.CREDITS,
        basketId: basketId,
        paymentMethodDetails: {},
        relatedServiceId,
      });
      onSuccess?.({
        paymentMethod: PaymentMethodType.CREDITS,
        provider: "company_operations",
        isPaymentConfirmed: true,
      });
      return response;
    } catch (error) {
      console.log({ error });
      throw error;
    }
  };

  return (
    <>
      <Wizard>
        <WizardNav>
          <BackLink onClick={onLeave} />
        </WizardNav>
        <WizardHeader>
          <Heading>
            <FormattedMessage {...checkTitle()} />
          </Heading>
        </WizardHeader>
        <WizardContent>
          <Container maxW={["container.lg", "container.md"]} padding="0">
            <Stack
              direction={["column", "column"]}
              spacing={["6", "6", "8"]}
              marginBottom="5"
            >
              <Card>
                <>
                  <OrderDescription order={orderPreview} />
                  {!hasBasketSubscription &&
                    !currentUser?.currentSubscription && (
                      <ServiceFeeSubscriptionCTA
                        serviceOfferId={serviceOfferId}
                        onClick={handleServiceFeeSubscriptionCTAClick}
                        mb={5}
                      />
                    )}
                  <ResponsiveFixedButton
                    data-testid="confirm-order"
                    colorScheme="seaGreen"
                    width="full"
                    size="lg"
                    onClick={handleConfirm}
                  >
                    <FormattedMessage {...messages.proceedToPayment} />
                  </ResponsiveFixedButton>
                  {context === "serviceClosureFlow" &&
                    !hasBasketSubscription &&
                    !service?.serviceRequest?.recurrent && (
                      <OppositionLink
                        url={generateRoute(
                          [
                            "SERVICE_OFFERS",
                            "SERVICE_OFFER",
                            "LEGACY_PAYMENT_OPPOSITION",
                          ],
                          { serviceOfferId }
                        )}
                      />
                    )}
                </>
              </Card>
            </Stack>
            <RingtwicePlusAdvantagesModal
              isOpen={isOpen}
              handleConfirm={handleContinueFromModal}
              onClose={() => setIsOpen(false)}
              isMobile={isMobile}
            />
            {isMobile && (
              <Box marginTop="10">
                <FAQSection />
              </Box>
            )}
          </Container>
        </WizardContent>
      </Wizard>
      {!isMobile && (
        <Container
          marginY={[10, 10, 20]}
          maxW={["container.lg", "container.md", "container.lg"]}
        >
          <FAQSection />
        </Container>
      )}
    </>
  );
};
